import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'secondary' | 'card'
  size?: 'normal' | 'big'
  fullWidth?: boolean
  loading?: boolean
  icon?: React.ReactNode
  description?: string
  label?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'normal',
      fullWidth = false,
      loading = false,
      disabled = false,
      icon,
      label,
      description,
      className = '',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    if (variant === 'card') {
      return (
        <button
          ref={ref}
          disabled={isDisabled}
          className={`
            flex flex-col items-center justify-center p-8 rounded-2xl bg-white
            border-2 border-gray-lighter cursor-pointer transition-all duration-200
            shadow-sm min-w-[180px]
            hover:border-primary hover:shadow-card hover:-translate-y-0.5
            active:translate-y-0 active:shadow-sm
            disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0
            ${className}
          `}
          {...props}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              {icon && (
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-3.5 transition-all duration-200 shadow-lg">
                  {icon}
                </div>
              )}
              {label && (
                <span className="text-base font-bold text-center text-gray-dark uppercase tracking-wide">
                  {label}
                </span>
              )}
              {description && (
                <span className="text-sm text-center text-gray mt-1.5 max-w-[240px] leading-relaxed">
                  {description}
                </span>
              )}
            </>
          )}
        </button>
      )
    }

    const baseClasses =
      "inline-flex items-center justify-center gap-2 rounded-[50px] font-semibold font-['Source Sans Pro',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif] cursor-pointer transition-all duration-200 min-w-[120px] border-none outline-none disabled:cursor-not-allowed"

    const variantClasses = {
      primary: `
        bg-black text-white border-none shadow-[0_4px_12px_#0000001a]
        hover:bg-black/85
        active:shadow-md
        disabled:opacity-60 disabled:shadow-none
      `,
      outline: `
        bg-transparent border border-primary text-primary
        hover:bg-primary/90 hover:text-white
        active:shadow-md
        disabled:opacity-50
      `,
      secondary: `
        bg-[#E65300] text-white border-none shadow-[0_4px_12px_#E653004d]
        hover:bg-[#E65300]/90
        active:shadow-md
        disabled:opacity-60 disabled:shadow-none
      `
    }

    const sizeClasses = {
      normal: 'px-[28px] py-[14px] text-[16px] min-w-[120px]',
      big: 'px-10 py-5 text-lg min-w-[140px]'
    }

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          ${baseClasses}
          ${variantClasses[variant as keyof typeof variantClasses] || variantClasses.primary}
          ${sizeClasses[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : children || label}
      </button>
    )
  }
)

Button.displayName = 'Button'
